import React, { useEffect, useRef, useState } from 'react';

/**
 * Cinematic typography reveal — splits children text into characters
 * (or words) and animates each in with a small stagger and vertical lift.
 *
 * Usage:
 *   <RevealText>EINE EINZIGARTIGE MÖGLICHKEIT</RevealText>
 *   <RevealText as="span" delay={200} stagger={28}>BUILT ON STRUCTURE</RevealText>
 *
 * `mode` defaults to "char". Use "word" for long body text.
 */
export default function RevealText({
  children,
  as: Tag = 'span',
  delay = 0,
  stagger = 22,
  mode = 'char',
  className = '',
  ...rest
}) {
  const ref = useRef(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setActive(true), delay);
          io.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [delay]);

  const text = typeof children === 'string' ? children : '';
  const units =
    mode === 'word' ? text.split(/(\s+)/) : Array.from(text);

  return (
    <Tag
      ref={ref}
      className={`reveal-text ${className}`}
      data-testid="reveal-text"
      {...rest}
    >
      {units.map((u, i) => {
        if (/^\s+$/.test(u)) return <span key={i}>{u}</span>;
        return (
          <span
            key={i}
            aria-hidden={!active}
            style={{
              display: 'inline-block',
              transform: active ? 'translateY(0)' : 'translateY(0.55em)',
              opacity: active ? 1 : 0,
              transition: `transform 800ms cubic-bezier(0.22, 1, 0.36, 1) ${i * stagger}ms,
                           opacity 600ms ease ${i * stagger}ms`,
              willChange: 'transform, opacity',
            }}
          >
            {u === ' ' ? '\u00A0' : u}
          </span>
        );
      })}
      {/* Screen-readers read the full string */}
      <span className="sr-only" style={{ position: 'absolute', left: '-9999px' }}>
        {text}
      </span>
    </Tag>
  );
}
